import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionService } from '../auth/session.service';
import {
  AuthorizedOrg,
  AuthorizedOrgResponse,
  AuthProfile,
  AuthProfileResponse,
  OrgContext,
  OrgContextResponse,
} from './auth-profile.model';

const toOrgContext = (r: OrgContextResponse): OrgContext => ({
  orgId: r.org_id,
  orgName: r.org_name,
  locationManagementEnabled: r.location_management_enabled,
  dataTypes: r.data_types,
  locationIds: r.location_ids,
  accessLevel: r.access_level,
});

/** Unreported (null/absent) per-org values are left unset so the mapper falls back to
 *  the switch-org context. `role_id` is ignored — the role string is authoritative. */
const toAuthorizedOrg = (o: AuthorizedOrgResponse): AuthorizedOrg => ({
  orgId: o.org_id,
  orgName: o.org_name,
  orgLogo: o.org_logo ?? null,
  role: o.role ?? '',
  ...(o.data_types == null ? {} : { dataTypes: o.data_types }),
  ...(o.location_ids == null ? {} : { locationIds: o.location_ids }),
  ...(o.location_management_enabled == null
    ? {}
    : { locationManagementEnabled: o.location_management_enabled }),
});

/**
 * Auth/org profile API (FDD §4.2). Provides the signed-in user's authorized orgs
 * and the active org's scoped config. Targets the live CSD API; the OIDC bearer is
 * attached by the auth interceptor.
 */
@Injectable({ providedIn: 'root' })
export class AuthProfileService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly base = `${environment.API_URL}/auth`;

  /** Current user profile + the organizations they may access. */
  getProfile(): Observable<AuthProfile> {
    return this.http.get<AuthProfileResponse>(`${this.base}/me`).pipe(
      map((r) => ({
        userId: r.user_id,
        activeOrgId: r.active_org_id ?? null,
        authorizedOrgs: (r.authorized_orgs ?? []).map(toAuthorizedOrg),
      })),
    );
  }

  /** Validate access to an org and return its scoped context (sets it active). Sends
   *  the current audit session id, and adopts the one the response returns. */
  switchOrg(orgId: number): Observable<OrgContext> {
    const sessionId = this.session.currentId();
    return this.http
      .post<OrgContextResponse>(`${this.base}/switch-org`, {
        org_id: orgId,
        ...(sessionId === null ? {} : { session_id: sessionId }),
      })
      .pipe(
        tap((r) => {
          if (r.session_id != null) {
            this.session.adopt(r.session_id);
          }
        }),
        map(toOrgContext),
      );
  }
}
